import { resources } from "@constants/hardcoded-strings";
import { templateAvailabilityLevelOptions } from "@constants/template-availability-level-options";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group"
import { OrganizationSelectFormGroup } from "@containers/common/select-form-groups";
import { LabelWidthTypes } from "@enums/label-width-types";
import { TemplateAvailabilityLevelTypes } from "@enums/template-availability-level-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types"
import { IDWSession } from "@idw-types/data/idw-session";
import { FormattedMessageTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/message-template";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { isAxnAdminUser } from "@utils/permissions-util";
import { mapEntityWithNameAndIdToSelectOption } from "@utils/select-option-mapping-util";
import cloneDeep from "lodash/cloneDeep";
import { useMemo } from "react";
import { MessageTemplateGeneralInfoValidationErrors } from "@idw-types/common/validation/message-template/message-template-validation-errors";
import { isNullOrUndefined } from "@utils/object-util";
import { MessageTemplateTypes } from "@enums/message-template-types";

type TemplateAvailabilityLevelSectionProps = {
    isDisabled: boolean;
    session: IDWSession;
    state: FormattedMessageTemplate;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    labelWidth: LabelWidthTypes;
    serviceOrganizationId: string;
    validationErrors: MessageTemplateGeneralInfoValidationErrors;
    newTemplate: boolean;
    setState: (state: FormattedMessageTemplate) => void;
}

export const TemplateAvailabilityLevelSection = (props: TemplateAvailabilityLevelSectionProps) => {
    const { isDisabled, state, axnCompanies, selfServiceVerificationWhiteLabeledEmails, labelWidth, session, serviceOrganizationId, validationErrors, newTemplate, setState } = props;

    const handleUpdateTemplateAvailabilityLevel = (selectedOption: SelectOption<TemplateAvailabilityLevelTypes>) => {
        const updatedState = cloneDeep(state);
        if (selectedOption.value === TemplateAvailabilityLevelTypes.GLOBAL) {
            updatedState.company = null;
        } else if (selectedOption.value === TemplateAvailabilityLevelTypes.ORGANIZATION) {
            if (axnCompanies.length === 1) {
                updatedState.company = axnCompanies[0];
            } else if (serviceOrganizationId) {
                updatedState.company = axnCompanies.find(company => company.id === serviceOrganizationId);
            }
        }

        setState({
            ...updatedState,
            templateAvailabilityLevel: selectedOption.value
        });
    }

    const handleUpdateCompany = (newValue: SelectOption) => {
        const newCompany = newValue ? axnCompanies.find(company => company.id === newValue.value) : null;
        setState({
            ...state,
            company: newCompany,
        });
    };

    const handleUpdateSender = (newValue: SelectOption) => {
        setState({
            ...state,
            sender: newValue.value,
        });
    };

    const isAdminUser = useMemo(() => isAxnAdminUser(session.user), [session.user]);

    const isOrgUser = useMemo(() => {
        return !isAdminUser;
    }, [session.user]);

    const possibleTemplateAvailabilityLevelOptions: Array<SelectOption<TemplateAvailabilityLevelTypes>> = useMemo(() => {
        return templateAvailabilityLevelOptions.filter(option => {
            if (isOrgUser && option.value === TemplateAvailabilityLevelTypes.GLOBAL) {
                return false;
            } else if (option.value === TemplateAvailabilityLevelTypes.RELYING_PARTY) {
                return false;
            } else {
                return true;
            }
        });
    }, [session.user]);

    const filterEmailOptions = (companyId: string): Array<SelectOption> => {
        const filteredEmails = selfServiceVerificationWhiteLabeledEmails
            .filter(email => email.organizationID === companyId || (isNullOrUndefined(email.organizationID) && isNullOrUndefined(email.rpserviceID)))
            .flatMap(email => {
                let emails: Array<string> = [];
                try {
                    const parsedEmail = JSON.parse(email.email);
                    if (Array.isArray(parsedEmail)) {
                        emails = parsedEmail;
                    } else {
                        emails = [parsedEmail];
                    }
                } catch (error) {
                    console.error(`error parsing email json:`, error);
                    emails = [email.email];
                }

                return emails.map(e => ({
                    label: e,
                    value: e
                }));
            });
        return filteredEmails;
    };

    const selectedTemplateAvailabilityLevelOption = useMemo(() => {
        if (state.templateAvailabilityLevel) {
            return possibleTemplateAvailabilityLevelOptions.find(level => level.value === state.templateAvailabilityLevel);
        }

        return null;
    }, [possibleTemplateAvailabilityLevelOptions, state.templateAvailabilityLevel]);


    const selectedCompany: SelectOption = useMemo(() => {
        if (state.company) {
            return mapEntityWithNameAndIdToSelectOption(state.company);
        }
        return null;
    }, [axnCompanies, state.company]);


    const resource = resources.modals.addMessageTemplate;

    return (<div>
        <SelectFormGroup
            controlId={`message-template-template-availibility-level-select`}
            automationId={`message-template-template-availibility-level-select`}
            label={resource.inputs.templateAvailabilityLevel.label}
            ariaLabel={resource.inputs.templateAvailabilityLevel.label}
            onChange={handleUpdateTemplateAvailabilityLevel}
            options={possibleTemplateAvailabilityLevelOptions}
            selectedOption={selectedTemplateAvailabilityLevelOption}
            disabled={isDisabled || possibleTemplateAvailabilityLevelOptions.length === 1 || (!isAdminUser && newTemplate)}
            isRequired={true}
            isSearchable={false}
            isClearable={false}
            labelWidth={labelWidth}
        />
        {validationErrors.templateAvailabilityLevelError}

        {[TemplateAvailabilityLevelTypes.ORGANIZATION].includes(state.templateAvailabilityLevel) &&
            <>
                <OrganizationSelectFormGroup
                    idPrefix={`message-template-org-select`}
                    onChange={handleUpdateCompany}
                    axnCompanies={axnCompanies}
                    selectedOption={selectedCompany}
                    disabled={axnCompanies.length === 1 || isDisabled || (!isAdminUser && newTemplate)}
                    isRequired={true}
                    isSearchable={true}
                    isClearable={axnCompanies.length > 1}
                    labelWidth={labelWidth}
                />


            </>}
        {validationErrors.companyError}
        {state.type == MessageTemplateTypes.EMAIL && (
            <SelectFormGroup
                controlId="message-template-sender-input"
                automationId="message-template-sender-input"
                label={resource.inputs.sender.label}
                ariaLabel={resource.inputs.sender.label}
                placeholder={resource.inputs.sender.placeholder}
                options={selectedCompany ? filterEmailOptions(selectedCompany.value) : []}
                selectedOption={state.sender ? { label: state.sender, value: state.sender } : null}
                onChange={handleUpdateSender}
                disabled={!selectedCompany || selectedCompany.value === TemplateAvailabilityLevelTypes.GLOBAL || filterEmailOptions(selectedCompany.value).length <= 1 || isDisabled}
                isRequired={true}
                labelWidth={labelWidth}
                isSearchable={false}
                isClearable={false}
            />
        )}
        {validationErrors.senderError}
    </div>);
}